import { Icon } from '@iconify/react';
import Critter from '../glyphs/Critter';
import Glyph from '../glyphs/Glyph';
import { motion } from 'framer-motion';
import { feelingById } from '../../constants/bridge';
import { skyFor } from '../../constants/sky';
import type { HomeSignal } from '../../hooks/useHomeSignals';

interface Props {
  name: string;
  avatarId?: string | null;
  online: boolean;
  signal?: HomeSignal;
  index: number;
  onOpen: () => void;
}

const ago = (iso: string | null) => {
  if (!iso) return null;
  const h = Math.round((Date.now() - new Date(iso).getTime()) / 36e5);
  if (h < 1) return 'עכשיו';
  if (h < 24) return `לפני ${h} ש׳`;
  const d = Math.round(h / 24);
  return d === 1 ? 'אתמול' : `לפני ${d} ימים`;
};

/**
 * A patient as a small window onto their sky: the colour is how they said they
 * feel, the planks are today's notes done. Readable across a room of windows.
 */
export default function PatientWindow({ name, avatarId, online, signal, index, onOpen }: Props) {
  const feeling = signal?.feeling ?? null;
  const tone = skyFor(feeling, 12);
  const f = feelingById(feeling);
  const total = signal?.total ?? 0;
  const done = signal?.done ?? 0;
  const n = Math.max(total, 1);

  return (
    <motion.button
      onClick={onOpen}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      whileHover={{ y: -4 }}
      className="group text-start rounded-[26px] bg-card border border-border overflow-hidden shadow-[0_1px_0_hsl(var(--border))] hover:shadow-[0_16px_30px_-18px_rgba(47,38,30,0.5)] transition-shadow"
    >
      <div className="relative h-28" style={{ background: `linear-gradient(${tone.top}, ${tone.bottom})` }}>
        <svg viewBox="0 0 300 112" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden="true">
          {(feeling === 'sunny' || feeling === 'breezy' || !feeling) && <circle cx="250" cy="44" r="14" fill="#ffd36e" opacity={feeling ? 1 : 0.5} />}
          {feeling && feeling !== 'sunny' && (
            <g fill={feeling === 'stormy' ? '#5b6170' : feeling === 'rainy' ? '#9aa6b1' : '#ffffff'} opacity="0.95">
              <ellipse cx="200" cy="40" rx="26" ry="10" />
              <circle cx="190" cy="34" r="10" />
              <circle cx="206" cy="30" r="13" />
              {feeling !== 'breezy' && <><ellipse cx="80" cy="30" rx="20" ry="8" /><circle cx="86" cy="24" r="10" /></>}
            </g>
          )}
          {(feeling === 'rainy' || feeling === 'stormy') && (
            <g stroke="#eef3f7" strokeWidth="1.2" strokeLinecap="round" opacity="0.8">
              {Array.from({ length: 14 }).map((_, i) => (
                <line key={i} x1={20 + i * 20} y1={50 + (i % 3) * 8} x2={16 + i * 20} y2={60 + (i % 3) * 8} />
              ))}
            </g>
          )}
          {feeling === 'stormy' && <path d="M140 46 l-6 12 h6 l-4 12 l12 -16 h-6 l4 -8 z" fill="#ffd36e" />}
          <path d="M0 86 C40 74 70 80 92 98 L100 112 L0 112 Z" fill={tone.near} />
          <path d="M300 82 C260 72 228 80 206 98 L198 112 L300 112 Z" fill={tone.near} />
          <rect x="0" y="100" width="300" height="12" fill={tone.water} opacity="0.7" />
          {Array.from({ length: total }).map((_, i) => {
            const t = (i + 0.5) / n;
            const x = 92 + 114 * t;
            const y = 96 - Math.sin(Math.PI * t) * 14;
            const w = 114 / n - 3;
            return (
              <rect key={i} x={x - w / 2} y={y - 3} width={w} height={6} rx={2}
                fill={i < done ? '#f0a43a' : tone.dark ? '#ffffff33' : '#ffffff99'} />
            );
          })}
        </svg>
        {f && (
          <span className={`absolute top-3 start-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold backdrop-blur ${tone.dark ? 'bg-white/15 text-white' : 'bg-white/60 text-foreground'}`}>
            <Glyph name={f.glyph} size={14} />
            {f.label}
            <span className="opacity-70 font-medium">· {ago(signal?.feelingAt ?? null)}</span>
          </span>
        )}
        {!f && (
          <span className="absolute top-3 start-3 rounded-full bg-white/60 px-2.5 py-1 text-[11px] font-bold text-foreground">
            עוד לא שיתף/ה מזג אוויר
          </span>
        )}
        {(signal?.waiting ?? 0) > 0 && (
          <span className="absolute top-3 end-3 inline-flex items-center gap-1 rounded-full bg-accent text-accent-foreground px-2.5 py-1 text-[11px] font-bold">
            <Glyph name="note" size={14} />
            {signal!.waiting} חדשים
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 px-4 py-3">
        <div className="relative -mt-9 w-14 h-14 rounded-2xl bg-card border-4 border-card shadow-sm flex items-center justify-center shrink-0">
          <Critter id={avatarId} size={46} />
          {online && <span className="absolute -bottom-0.5 -end-0.5 w-3.5 h-3.5 rounded-full bg-success border-2 border-card" aria-label="מחובר/ת עכשיו" />}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-extrabold text-foreground truncate">{name}</h3>
          <p className="text-xs text-muted-foreground">
            {total > 0 ? `${done} מתוך ${total} פתקים היום` : 'אין פתקים פעילים'}
          </p>
        </div>
        <Icon icon="solar:alt-arrow-left-linear" className="text-xl text-muted-foreground group-hover:text-primary transition-colors" aria-hidden="true" />
      </div>
    </motion.button>
  );
}
